import React from "react"

import { CheckListItem, Divider } from "@components"
import { getEventScheduleState, mySqlToLocal } from "@helpers"
import { useEventMenu } from "@hooks"

import {
  BookmarkSlashIcon,
  Cog8ToothIcon,
  PaperAirplaneIcon,
  UserGroupIcon,
  UsersIcon,
} from "@heroicons/react/24/outline"

import { useNavigate } from "react-router-dom"

import PropTypes from "prop-types"

const EventCheckList = ({
  bookingsDisabled,
  event,
  eventCreated,
  noOfInvitationsPending = 0,
  noOfInvitationsSent = 0,
  noOfBookingConfirmationsPending = 0,
  noOfBookingConfirmationsSent = 0,
  noOfSessions = 0,
  noOfStudents = 0,
  openToParents,
  openToStaff,
  user,
}) => {
  const getEmailStatus = (sent, pending) => {
    if (sent === 0) return "warning"
    if (pending === 0) return "success"
    if (pending > 0) return "warning"
  }
  
  const navigate = useNavigate()
  const { open: openEventMenu } = useEventMenu(user, event)
  const { openStaffSchedule, openParentsSchedule, disabledParentBookingsSchedule } = getEventScheduleState(event)
  
  return (
    <React.Fragment>
      <CheckListItem
        icon={<Cog8ToothIcon className="w-10 h-10 mr-2 text-indigo-800 dark:text-foreground" />}
        status={eventCreated ? "success" : "danger"}
        title="Event Created"
        meta={`${noOfSessions} sessions | ${noOfStudents} students.`}
        required={true}
      />

      <Divider className="my-3" />

      <CheckListItem
        handleClick={() => openEventMenu("ACCESS")}
        icon={<UserGroupIcon className="w-10 h-10 mr-2 text-indigo-800 dark:text-foreground" />}
        status={openToStaff ? "success" : "danger"}
        title="Open to Staff"
        meta={openStaffSchedule ? `Scheduled to open on ${mySqlToLocal(openStaffSchedule.execute_at).format("DD MMM [at] HH:mm")}` : `Is this event open to staff.`}
        required={true}
      />

      <Divider className="my-3" />

      <CheckListItem
        handleClick={() => openEventMenu("ACCESS")}
        icon={<UsersIcon className="w-10 h-10 mr-2 text-indigo-800 dark:text-foreground" />}
        status={openToParents ? "success" : "danger"}
        title="Open to Parents"
        meta={openParentsSchedule ? `Scheduled to open on ${mySqlToLocal(openParentsSchedule.execute_at).format("DD MMM [at] HH:mm")}` : `Is this event open to parents.`}
        required={true}
      />

      <Divider className="my-3" />

      <CheckListItem
        handleClick={() => openEventMenu("ACCESS")}
        icon={<BookmarkSlashIcon className="w-10 h-10 mr-2 text-indigo-800 dark:text-foreground" />}
        status={bookingsDisabled ? "success" : "warning"}
        title="Bookings Disabled"
        meta={disabledParentBookingsSchedule ? `Scheduled to disable on ${mySqlToLocal(disabledParentBookingsSchedule.execute_at).format("DD MMM [at] HH:mm")}` : `Disable parent bookings just prior to the event.`}
        required={false}
      />

      <Divider className="my-3" />

      <CheckListItem
        handleClick={() => navigate(`/event/${event.id}/invitations`)}
        icon={<PaperAirplaneIcon className="w-10 h-10 mr-2 text-indigo-800 dark:text-foreground" />}
        status={getEmailStatus(noOfInvitationsSent, noOfInvitationsPending)}
        title="Invitation Emails Sent"
        meta={`${noOfInvitationsPending} Pending | ${noOfInvitationsSent} Sent`}
        required={false}
      />

      <Divider className="my-3" />

      <CheckListItem
        handleClick={() => navigate(`/event/${event.id}/communications`)}
        icon={<PaperAirplaneIcon className="w-10 h-10 mr-2 text-indigo-800 dark:text-foreground" />}
        status={getEmailStatus(noOfBookingConfirmationsSent, noOfBookingConfirmationsPending)}
        title="Booking Confirmation Emails Sent"
        meta={`${noOfBookingConfirmationsPending} Pending | ${noOfBookingConfirmationsSent} Sent`}
        required={false}
      />
    </React.Fragment>
  )
}

EventCheckList.propTypes = {
  bookingsDisabled: PropTypes.bool.isRequired,
  eventCreated: PropTypes.bool.isRequired,
  event: PropTypes.object.isRequired,
  noOfSessions: PropTypes.number,
  noOfStudents: PropTypes.number,
  noOfInvitationsPending: PropTypes.number,
  noOfInvitationsSent: PropTypes.number,
  noOfBookingConfirmationsPending: PropTypes.number,
  noOfBookingConfirmationsSent: PropTypes.number,
  openToParents: PropTypes.bool.isRequired,
  openToStaff: PropTypes.bool.isRequired,
  user: PropTypes.object.isRequired,
}

export default EventCheckList
